import { IDeploymentOptions } from 'dto/deployment/deployment-dto';
import { ComponentDto } from 'dto/raptor-component.dto';
import { ComponentVersionDto } from 'dto/raptor-component-version.dto';

import {
    IRaptorComponentCompareState,
    IRaptorComponentCompareVersion,
    RaptorComponentCompareStatus,
} from '../deployment-create.types';
import { localeCompare } from 'common-utils/string-utils';

export class RaptorDeploymentHelpers {

    private static isSameComponentVersion(
        targetVersion?: IRaptorComponentCompareVersion,
        currentVersion?: IRaptorComponentCompareVersion,
    ): boolean {
        if (!targetVersion || !currentVersion) return false;
        if (targetVersion.componentVersionUuid && currentVersion.componentVersionUuid) {
            return targetVersion.componentVersionUuid === currentVersion.componentVersionUuid;
        }
        return targetVersion.version === currentVersion.version;
    }

    public static toCompareVersion(
        component: ComponentDto,
        componentVersion: ComponentVersionDto,
    ): IRaptorComponentCompareVersion {
        return {
            componentUuid: component.uuid,
            componentName: component.name,
            componentVersionUuid: componentVersion.uuid,
            version: componentVersion.version,
        };
    }

    public static extractComponentVersionCandidates(
        deployment: IDeploymentOptions,
    ): string[] {
        return (deployment.deploymentUnits || []).flatMap((unit) => {
            return unit.componentVersions.map(item => item.uuid);
        });
    }

    public static buildCompareState(
        targetLabel: string,
        currentLabel: string,
        targetVersions: IRaptorComponentCompareVersion[],
        currentVersions: IRaptorComponentCompareVersion[],
    ): IRaptorComponentCompareState {
        const targetVersionMap = new Map(
            targetVersions.map((version) => [version.componentUuid, version]),
        );
        const currentVersionMap = new Map(
            currentVersions.map((version) => [version.componentUuid, version]),
        );
        const keys = Array.from(new Set([...targetVersionMap.keys(), ...currentVersionMap.keys()])).sort((left, right) => {
            const leftVersion = targetVersionMap.get(left) || currentVersionMap.get(left);
            const rightVersion = targetVersionMap.get(right) || currentVersionMap.get(right);
            return localeCompare(leftVersion?.componentName ?? '', rightVersion?.componentName ?? '');
        });
        const targetItems: IRaptorComponentCompareState['target']['items'] = [];
        const currentItems: IRaptorComponentCompareState['current']['items'] = [];

        keys.forEach((key, index) => {
            const targetVersion = targetVersionMap.get(key);
            const currentVersion = currentVersionMap.get(key);
            const status = targetVersion && currentVersion
                ? RaptorDeploymentHelpers.isSameComponentVersion(targetVersion, currentVersion)
                    ? RaptorComponentCompareStatus.NO_CHANGE
                    : RaptorComponentCompareStatus.CHANGE
                : targetVersion
                    ? RaptorComponentCompareStatus.ADD
                    : RaptorComponentCompareStatus.REMOVE;

            targetItems.push({
                index,
                status,
                componentVersion: targetVersion,
            });
            currentItems.push({
                index,
                status,
                componentVersion: currentVersion,
            });
        });

        return {
            target: {
                label: targetLabel,
                items: targetItems,
            },
            current: {
                label: currentLabel,
                items: currentItems,
            },
        };
    }

    public static removeVersionsForUnselectedComponents(
        selectedComponentVersions: Record<string, ComponentVersionDto>,
        selectedComponents: ComponentDto[],
    ): Record<string, ComponentVersionDto> {
        const selectedComponentUuids = new Set(selectedComponents.map((component) => component.uuid));
        return Object.fromEntries(
            Object.entries(selectedComponentVersions).filter(([componentUuid]) =>
                selectedComponentUuids.has(componentUuid),
            ),
        );
    }

    public static reconcileSelectedVersions(
        selectedComponents: ComponentDto[],
        selectedComponentVersions: Record<string, ComponentVersionDto>,
        availableVersions: Record<string, ComponentVersionDto[]>,
    ): Record<string, ComponentVersionDto> {
        const nextSelectedComponentVersions = { ...selectedComponentVersions };
        selectedComponents.forEach((component) => {
            const versions = availableVersions[component.uuid] || [];
            const selectedVersion = nextSelectedComponentVersions[component.uuid];
            if (!versions.some((version) => version.uuid === selectedVersion?.uuid)) {
                if (versions[0]) nextSelectedComponentVersions[component.uuid] = versions[0];
                else delete nextSelectedComponentVersions[component.uuid];
            }
        });

        return nextSelectedComponentVersions;
    }
}
